import React, { useRef, useState, useEffect } from 'react'
import cn from 'classnames'
import {
  PublicIcon,
  MoreHorizIcon,
  CancelledIcon,
  ThumbUpIcon,
  ChatBubbleOutlineIcon,
  FacebookShareIcon,
} from 'images'
import { useTranslation } from 'react-i18next'

export function FacebookPreview({ data, placement, label, onLoaded, mobileView = false }) {
  const { t } = useTranslation()
  const { page_name, profile_picture_url, body, media, headline, call_to_action, destination_url } =
    data
  const bodyRef = useRef(null)
  const [isLongText, setIsLongText] = useState(false)
  const [showMore, setShowMore] = useState(false)

  useEffect(() => {
    if (bodyRef) setIsLongText(bodyRef.current.scrollHeight > bodyRef.current.clientHeight)
  }, [])

  if (!data) return null

  const toggleShowMore = () => {
    setShowMore((prevState) => !prevState)
  }

  // Aspect ratio calculation
  let aspectClass = 'ratio-4x5'
  if (media && media.width && media.height) {
    const ratio = media.width / media.height
    if (Math.abs(ratio - 1) < 0.13) {
      aspectClass = 'ratio-1x1'
    } else if (ratio > 1.4) {
      // Landscape (16:9 or similar)
      aspectClass = 'ratio-16x9'
    } else if (Math.abs(ratio - 0.8) < 0.13) {
      aspectClass = 'ratio-4x5'
    }
  }

  return (
    <div className="feature-card-container">
      <div className="feature-card">
        <div
          className={cn('placement', 'placement-fb-feed', {
            mobile: mobileView,
            desktop: !mobileView,
            'landscape-media': aspectClass === 'ratio-16x9',
            'long-text': bodyRef.current?.scrollHeight > 36,
          })}
        >
          {/* Header */}
          <div className="feed-header">
            <div className="profile-pic">
              {profile_picture_url ? (
                <img alt={page_name} className="profile_picture" src={profile_picture_url} />
              ) : (
                <div className="icon" />
              )}
            </div>
            <div className="profile-info">
              <div className="page-name">{page_name || 'Page Name'}</div>
              <div className="sponsored">
                Sponsored
                <span>
                  <PublicIcon width={15} />
                </span>
              </div>
            </div>
            <div className="actions d-flex align-items-baseline gap-1">
              <span>
                <MoreHorizIcon width={mobileView ? 24 : 30} />
              </span>
              {mobileView && (
                <span>
                  <CancelledIcon width={18} />
                </span>
              )}
            </div>
          </div>

          {/* Post Text */}
          <p className="feed-text">
            <span ref={bodyRef} className={`${showMore ? 'text' : 'text_clipped'}`}>
              {body}
            </span>
          </p>
          {mobileView && isLongText && (
            <span className="more" onClick={toggleShowMore}>
              {showMore ? 'See less' : 'See more'}
            </span>
          )}

          {/* Main content area: media + order-now */}
          <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
            {/* Creative (choose ratio class) */}
            <div className={`feed-media ${aspectClass}`} style={{ flexShrink: 0 }}>
              {media && media.url ? (
                media.type && media.type.includes('video') ? (
                  <video
                    className="media"
                    src={media.url}
                    autoPlay
                    loop
                    muted
                    playsInline
                    onLoad={onLoaded}
                  />
                ) : (
                  <img
                    src={media.url}
                    alt={media.filename || 'ad creative'}
                    height={media.height}
                    width={media.width}
                    className="media"
                    onLoad={onLoaded}
                  />
                )
              ) : (
                <img src="your-ad.jpg" alt="ad creative" />
              )}
            </div>

            {/* Headline, CTA, and URL */}
            <div
              className="order-now"
              style={{ flex: 1, display: 'flex', flexDirection: 'row', alignItems: 'stretch' }}
            >
              <div className="text order-now_container">
                <div className="url" title={destination_url}>
                  {destination_url || ''}
                </div>
                <div className="headline">{headline || ''}</div>
              </div>
              <div className="button cta" style={{ alignSelf: 'center', flex: 1 }}>
                {t(`Recommendations.campaign.setup.call_to_action.${call_to_action}`) || ''}
              </div>
            </div>
          </div>

          {/* Engagement Bar */}
          <div className="feed-engagement">
            <button>
              <ThumbUpIcon />
              <span>Like</span>
            </button>
            <button>
              <ChatBubbleOutlineIcon />
              <span>Comment</span>
            </button>
            <button>
              <FacebookShareIcon />
              <span>Share</span>
            </button>
          </div>
        </div>
      </div>
      <div className="feature-card-label">
        <span>{placement}</span>
        <span>{label}</span>
      </div>
    </div>
  )
}
